import React, { useEffect, useState } from 'react';
import { Form, Input, Button, Row, Col, App, Upload, Avatar, Typography, Spin, Space } from 'antd';
import { UserOutlined, UploadOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import type { RcFile } from 'antd/es/upload/interface';

const AccountInfoForm: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const { message } = App.useApp();
  const [formLoading, setFormLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | undefined>(user?.photo_url);
  const [username, setUsername] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<RcFile | null>(null);

  useEffect(() => {
    const loadProfile = async () => {
      setFormLoading(true);
      try {
        const response = await api.get('/profile');
        const apiData = response.data.data;

        // Bandingkan timestamp untuk menentukan data mana yang lebih baru
        const contextUpdatedAt = user?.updated_at ? new Date(user.updated_at).getTime() : 0;
        const apiUpdatedAt = apiData.updated_at ? new Date(apiData.updated_at).getTime() : 0;

        let dataToUse = apiData;
        // Jika data di context lebih baru, gunakan data context
        if (contextUpdatedAt > apiUpdatedAt) {
          dataToUse = user;
        }
        
        form.setFieldsValue({
          full_name: dataToUse.full_name,
          email: dataToUse.email,
          phone: dataToUse.phone,
          photo_url: dataToUse.photo_url,
        });
        
        setImageUrl(dataToUse.photo_url);
        setUsername(dataToUse.username);

      } catch (error) {
        console.error('Failed to fetch profile data:', error);
        message.error('Gagal memuat data profil.');
        // Fallback ke data context jika API gagal
        if (user) {
          form.setFieldsValue({
            full_name: user.full_name,
            email: user.email,
            phone: user.phone,
            photo_url: user.photo_url,
          });
          setImageUrl(user.photo_url);
          setUsername(user.username);
        }
      } finally {
        setFormLoading(false);
      }
    };

    loadProfile();
  }, [user, form, message]);

  const uploadFile = async () => {
    if (!selectedFile) {
      throw new Error('No file selected for upload.');
    }
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('path', 'profile');
    try {
      const response = await api.post('/upload', formData);
      return response.data.data.url;
    } catch (error) {
      message.error(`Gagal mengupload file: ${selectedFile.name}`);
      throw error;
    }
  };

  const onFinish = async (values: any) => {
    setSubmitLoading(true);
    try {
      let newImageUrl = values.photo_url; // Start with the value from the form

      if (selectedFile) {
        newImageUrl = await uploadFile();
      }

      const payload = {
        full_name: values.full_name,
        email: values.email,
        phone: values.phone || '',
        username: username,
        photo_url: newImageUrl || null, // Kirim null jika tidak ada foto
      };

      const response = await api.put('/profile', payload);
      message.success('Informasi akun berhasil diperbarui.');

      // Update context and local state
      const finalImageUrl = newImageUrl || undefined; // Gunakan undefined untuk state
      const updatedUser = { ...user!, ...response.data.data, photo_url: finalImageUrl };
      if (setUser) {
        setUser(updatedUser);
      }
      setImageUrl(finalImageUrl);
      
      navigate('/dashboard');
    } catch (error: any) {
      console.error('Failed to update profile:', error);
      message.error(error.response?.data?.message || 'Gagal memperbarui informasi akun.');
    } finally {
      setSubmitLoading(false);
    }
  };

  const beforeUpload = (file: RcFile) => {
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
    if (!isJpgOrPng) {
      message.error('Format file yang diizinkan hanya JPG, JPEG, PNG!');
    }
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error('Ukuran gambar harus lebih kecil dari 2MB!');
    }

    if (isJpgOrPng && isLt2M) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = e => setImageUrl(e.target?.result as string);
      reader.readAsDataURL(file);
    }

    return false; // Prevent automatic upload
  };

  const handleRemovePhoto = () => {
    setImageUrl(undefined);
    setSelectedFile(null);
    form.setFieldsValue({ photo_url: null }); // Set form value ke null
  };

  return (
    <Row justify="center">
      <Col xs={24} sm={20} md={16} lg={12}>
        <Spin spinning={formLoading}>
          <Form form={form} layout="vertical" onFinish={onFinish}>
            <Form.Item label="Foto Profil" style={{ marginBottom: 32 }}>
              <Space direction="vertical" align="center" style={{ width: '100%' }}>
                <Avatar size={128} icon={<UserOutlined />} src={imageUrl} />
                <Space>
                  <Upload
                    name="file"
                    beforeUpload={beforeUpload}
                    showUploadList={false}
                    accept=".jpg,.jpeg,.png"
                  >
                    <Button icon={<UploadOutlined />}>
                      Ubah Foto
                    </Button>
                  </Upload>
                  {imageUrl && (
                    <Button
                      icon={<DeleteOutlined />}
                      danger
                      onClick={handleRemovePhoto}
                    >
                      Hapus Foto
                    </Button>
                  )}
                </Space>
                <Typography.Text type="secondary" style={{ fontSize: 12, marginTop: 8 }}>
                  Format .jpg, .jpeg, .png. Ukuran maks 2MB.
                </Typography.Text>
              </Space>
            </Form.Item>

            <Form.Item name="photo_url" hidden>
              <Input />
            </Form.Item>

            <Form.Item
              name="full_name"
              label="Nama Lengkap"
              rules={[{ required: true, message: 'Nama lengkap wajib diisi!' }]}
            >
              <Input placeholder="Masukkan nama lengkap Anda" />
            </Form.Item>

            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: 'Email wajib diisi!' },
                { type: 'email', message: 'Format email tidak valid!' },
              ]}
            >
              <Input placeholder="Masukkan alamat email Anda" />
            </Form.Item>

            <Form.Item name="phone" label="No. Telepon">
              <Input placeholder="Masukkan nomor telepon Anda" />
            </Form.Item>

            <Form.Item>
              <Button type="primary" htmlType="submit" loading={submitLoading}>
                Simpan Perubahan
              </Button>
            </Form.Item>
          </Form>
        </Spin>
      </Col>
    </Row>
  );
};

export default AccountInfoForm;
